import { useEffect, useRef } from 'react'

/**
 * Scroll horizontal con barra arriba Y abajo.
 * Requiere que los Table de NextUI tengan la prop `removeWrapper`
 * para que este div sea el único scroll container.
 */
const DoubleScrollTable = ({ children }: { children: React.ReactNode }) => {
  const topRef = useRef<HTMLDivElement>(null)
  const mainRef = useRef<HTMLDivElement>(null)
  const phantomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const top = topRef.current
    const main = mainRef.current
    const phantom = phantomRef.current
    if (!top || !main || !phantom) return

    const syncWidth = () => {
      phantom.style.width = `${main.scrollWidth}px`
    }

    /* Mientras el sidebar se abre/cierra (transition de 0.3s en su ancho), el contenido se
       reacomoda en cada frame y el ResizeObserver dispara docenas de veces con anchos intermedios
       — como las tablas con layout automático no anchan sus columnas de forma monótona, el umbral
       de "scrollWidth > clientWidth" se cruza varias veces y la barra superior aparece/desaparece
       en pleno tránsito ("destella"). Debounce: solo sincroniza cuando el resize se asienta. */
    let debounceId: ReturnType<typeof setTimeout> | undefined
    const debouncedSyncWidth = () => {
      clearTimeout(debounceId)
      debounceId = setTimeout(syncWidth, 150)
    }

    const onTopScroll = () => { main.scrollLeft = top.scrollLeft }
    const onMainScroll = () => { top.scrollLeft = main.scrollLeft }

    top.addEventListener('scroll', onTopScroll, { passive: true })
    main.addEventListener('scroll', onMainScroll, { passive: true })

    const ro = new ResizeObserver(debouncedSyncWidth)
    ro.observe(main)
    syncWidth()

    return () => {
      clearTimeout(debounceId)
      ro.disconnect()
      top.removeEventListener('scroll', onTopScroll)
      main.removeEventListener('scroll', onMainScroll)
    }
  }, [])

  return (
    <div>
      {/* Barra superior — espejo */}
      <div
        ref={topRef}
        style={{ overflowX: 'auto', overflowY: 'hidden', height: '8px' }}
        className='dscroll-top mb-1'
      >
        <div ref={phantomRef} style={{ height: '1px' }} />
      </div>

      {/* Scroll container principal — provee barra inferior */}
      <div ref={mainRef} className='dscroll-main overflow-x-auto overflow-y-visible'>
        {children}
      </div>
    </div>
  )
}

export default DoubleScrollTable
